import React from "react";
import { Box, Typography, Button } from "@mui/material";
import {
  Inbox as InboxIcon,
  Lock as LockIcon,
  AddCircleOutline as AddIcon,
} from "@mui/icons-material";

interface EmptyPermissionStateProps {
  title?: string;
  message?: string;
  icon?: React.ReactNode;
  variant?: "empty" | "no-permission" | "no-data";
  actionLabel?: string;
  onAction?: () => void;
  showAction?: boolean;
}

/**
 * Empty state component for lists/pages
 * Shows different messages based on whether it's empty data or lack of permission
 */
export const EmptyPermissionState: React.FC<EmptyPermissionStateProps> = ({
  title,
  message,
  icon,
  variant = "empty",
  actionLabel,
  onAction,
  showAction = true,
}) => {
  // Default values based on variant
  const getDefaults = () => {
    switch (variant) {
      case "no-permission":
        return {
          icon: <LockIcon sx={{ fontSize: 64, color: "action.disabled" }} />,
          title: title || "Ei käyttöoikeutta",
          message:
            message ||
            "Sinulla ei ole oikeuksia nähdä tätä sisältöä. Ota yhteyttä järjestelmänvalvojaan.",
          showDefaultAction: false,
        };
      case "no-data":
        return {
          icon: <InboxIcon sx={{ fontSize: 64, color: "action.disabled" }} />,
          title: title || "Ei tietoja",
          message: message || "Tietoja ei löytynyt valituilla hakuehdoilla.",
          showDefaultAction: false,
        };
      case "empty":
      default:
        return {
          icon: <InboxIcon sx={{ fontSize: 64, color: "action.disabled" }} />,
          title: title || "Ei vielä sisältöä",
          message: message || "Aloita lisäämällä ensimmäinen kohde.",
          showDefaultAction: showAction && !!onAction,
        };
    }
  };

  const defaults = getDefaults();
  const displayIcon = icon || defaults.icon;
  const displayTitle = title || defaults.title;
  const displayMessage = message || defaults.message;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 300,
        py: 8,
        px: 3,
        textAlign: "center",
      }}
    >
      <Box sx={{ mb: 3 }}>{displayIcon}</Box>

      <Typography variant="h6" gutterBottom color="text.secondary">
        {displayTitle}
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ maxWidth: 400, mb: 3 }}
      >
        {displayMessage}
      </Typography>

      {defaults.showDefaultAction && onAction && (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAction}
          sx={{ mt: 1 }}
        >
          {actionLabel || "Lisää uusi"}
        </Button>
      )}
    </Box>
  );
};
